import type { IntlShape } from 'react-intl';

import type { MinMax } from '../../../../core/types/min-max.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type {
  AxisIdToFormatter,
  AxisIdToScaleDomain,
  MapDatapoints,
} from '../../../types/xy-chart-internals.js';
import {
  isAreaTooltipContext,
  type ClosestDatapoint,
  type ClosestSeries,
  type ClosestSeriesToMetadata,
  type TooltipContextClosestPoints,
} from '../../../utils/type-guards.js';
import { toRelative } from '../../../utils/value-representation.js';
import { getTooltipFormatter } from '../utils/get-tooltip-formatter.js';
import type { XYChartTooltipBodyProps } from '../XYChartTooltipBody/XYChartTooltipBody.js';
import { getClosestPointTooltipBodyContent } from './hooks/getClosestPointTooltipBodyContent.js';

export interface BuildClosestPointTooltipBodyPropsArgs<
  T extends ClosestSeries,
  K extends ClosestDatapoint<T>,
> {
  datapoint: K;
  series: T;
  seriesToMetadata: ClosestSeriesToMetadata<T>;
  axisIdToFormatter: AxisIdToFormatter;
  axisIdToScaleDomain: AxisIdToScaleDomain;
  datapointMetadataMap: MapDatapoints;
  intl: IntlShape;
}

export const buildClosestPointTooltipBodyProps = <
  T extends ClosestSeries,
  K extends ClosestDatapoint<T>,
>({
  datapoint,
  series,
  seriesToMetadata,
  axisIdToFormatter,
  axisIdToScaleDomain,
  datapointMetadataMap,
  intl,
}: BuildClosestPointTooltipBodyPropsArgs<T, K>): XYChartTooltipBodyProps => {
  const seriesMetadata = safeGet(seriesToMetadata, series);
  const enrichedDatapoint: TooltipContextClosestPoints = {
    series,
    datapoint,
    metadata: seriesMetadata,
  };

  const { xAxis, yAxis } = enrichedDatapoint.metadata;

  const xFormatter = getTooltipFormatter(xAxis, axisIdToFormatter, intl);
  const yFormatter = getTooltipFormatter(yAxis, axisIdToFormatter, intl);

  const datapointMetadata = safeGet(datapointMetadataMap, datapoint);
  const binDomain = datapointMetadata?.binDomain;

  //Base cases: Dot and Line series
  let xDomain = safeGet(axisIdToScaleDomain, xAxis.id) as MinMax;
  let yDomain = safeGet(axisIdToScaleDomain, yAxis.id) as MinMax;

  if (isAreaTooltipContext(enrichedDatapoint)) {
    const { isStacked } = enrichedDatapoint.metadata;

    if (isStacked && datapointMetadata.baseline !== 'x' && binDomain) {
      xDomain = { min: binDomain * -1, max: binDomain };
    }

    if (isStacked && datapointMetadata.baseline !== 'y' && binDomain) {
      yDomain = { min: binDomain * -1, max: binDomain };
    }
  }

  const { text: textX, duration: durationX } =
    getClosestPointTooltipBodyContent(
      [datapoint.x0, datapoint.x1],
      xFormatter,
      xAxis.isRelative ? (value) => toRelative(value, xDomain) : undefined,
    );
  const { text: textY, duration: durationY } =
    getClosestPointTooltipBodyContent(
      [datapoint.y0, datapoint.y1],
      yFormatter,
      yAxis.isRelative ? (value) => toRelative(value, yDomain) : undefined,
    );

  return {
    xAxisLabel: xAxis.label,
    yAxisLabel: yAxis.label,
    xAxisValue: textX,
    yAxisValue: textY,
    xAxisDuration: durationX,
    yAxisDuration: durationY,
  };
};
